/**
 * "Computed" tile grid for the loan editor drawer — matches the design's
 * `#mfTiles`. Always shows Loan Amount (highlighted) · Monthly EMI ·
 * Origination Fee, with a "More details" toggle revealing Monthly Interest,
 * Total Loan Cost, Total Interest, and Balloon Payment.
 */
import { useState } from "react";

import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

import type { LoanDraftMetrics } from "./financing-metrics";

function fmtCurrency(value: number): string {
  const sign = value < 0 ? "-" : "";
  return sign + "$" + Math.abs(Math.round(value)).toLocaleString();
}

interface FinancingComputedTilesProps {
  metrics: LoanDraftMetrics;
  termMonths: number;
}

interface Tile {
  label: string;
  value: string;
  highlight?: boolean;
}

export function FinancingComputedTiles({
  metrics,
  termMonths,
}: FinancingComputedTilesProps) {
  const [moreOpen, setMoreOpen] = useState(false);

  const termLabel =
    termMonths > 0 && termMonths % 12 === 0
      ? `${termMonths / 12}yr`
      : `${termMonths}mo`;

  const primary: Tile[] = [
    { label: "Loan Amount", value: fmtCurrency(metrics.loanAmount), highlight: true },
    {
      // "EMI" only fits a loan that actually amortizes. Hard money pays interest
      // and balloons the principal — calling that an EMI contradicts the Balloon
      // Payment and Total Interest tiles below.
      label: metrics.isAmortizing ? "Monthly EMI" : "Monthly Payment (Interest-Only)",
      value: fmtCurrency(metrics.monthlyEmi),
    },
    { label: "Origination Fee", value: fmtCurrency(metrics.originationFee) },
  ];

  const more: Tile[] = [
    // On an interest-only loan the carry IS the headline payment; only an
    // amortizing loan has a distinct interest portion worth breaking out.
    ...(metrics.isAmortizing
      ? [{ label: "Monthly Interest", value: fmtCurrency(metrics.monthlyInterest) }]
      : []),
    {
      label: `Total Loan Cost (${termLabel})`,
      value: fmtCurrency(metrics.totalLoanCost),
    },
    { label: "Total Interest", value: fmtCurrency(metrics.totalInterest) },
    { label: "Balloon Payment", value: fmtCurrency(metrics.balloonPayment) },
  ];

  return (
    <div className="grid grid-cols-2 gap-2.5">
      {primary.map((tile) => (
        <TileCard key={tile.label} tile={tile} />
      ))}

      <button
        type="button"
        onClick={() => setMoreOpen((v) => !v)}
        className="col-span-2 inline-flex items-center justify-center gap-1.5 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        {moreOpen ? "Hide details" : "More details"}
        <ChevronDown
          className={cn("h-3.5 w-3.5 transition-transform", moreOpen && "rotate-180")}
          aria-hidden
        />
      </button>

      {moreOpen &&
        more.map((tile) => <TileCard key={tile.label} tile={tile} />)}
    </div>
  );
}

function TileCard({ tile }: { tile: Tile }) {
  return (
    <div
      className={cn(
        "rounded-[10px] border p-3",
        tile.highlight
          ? "border-primary/30 bg-primary/10"
          : "border-border bg-muted/40",
      )}
    >
      <div
        className={cn(
          "flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider",
          tile.highlight ? "text-primary" : "text-muted-foreground",
        )}
      >
        {tile.label}
      </div>
      <div className="mt-1 text-lg font-extrabold leading-tight tabular-nums text-foreground">
        {tile.value}
      </div>
    </div>
  );
}
